import { useLayoutEffect, useRef, useState } from 'react';
import './CurvedTabs.css';

/**
 * CurvedTabs — a solid tab bar whose top edge rises in a smooth BUMP over the
 * active tab (ease-in-out S-curve shoulders), with a pill marking the active
 * tab (b2b-demo DS, #1108). Inspired by the "curved bump" nav pattern.
 *
 * The bar shape is an SVG <path> measured against the live container width (via
 * ResizeObserver) so the bump lands exactly over the active tab and re-flows on
 * resize. Tabs are equal-width. All colours via var(--widget-*, fallback) for
 * Shadow-DOM resilience; the bar defaults to the surface colour so it merges
 * into a card below it, and the active pill uses the theme primary.
 *
 * @param {object} props
 * @param {{ value: string, label: string }[]} props.tabs
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {string} [props.ariaLabel]
 * @param {string} [props.className] — extra class on the <nav> (positioning)
 */
// Vertical geometry is expressed as fractions of the measured strip height, so
// the whole bar scales when --curved-tabs-h changes (e.g. smaller tabs on small
// screens) without letterboxing the SVG. Ratios derived from the original 82px.
const BASE_H = 82;
const BAR_TOP_R = 28 / BASE_H; // flat bar top; the bump rises above it
const BUMP_TOP_R = 4 / BASE_H; // bump plateau y
const PILL_Y_R = 8 / BASE_H; // active-pill top
const PILL_H_R = 50 / BASE_H; // active-pill height
const OUTER_R = 0; // outer top corners square, flush to the strip edges (#1108)
const ARC = 0.5523; // cubic-bezier handle for a true circular quarter-corner

const buildBarPath = (w, h, activeIndex, count) => {
  if (!w || !h) return '';
  const BAR_TOP = h * BAR_TOP_R;
  const BUMP_TOP = h * BUMP_TOP_R;
  const tabW = w / count;
  const cx = tabW * (activeIndex + 0.5);
  // Wide, gentle shoulders → a smooth plush hump (not a tight step).
  const plateauHalf = tabW * 0.38;
  const shoulder = tabW * 0.32;
  const k = 0.62; // bezier handle length → horizontal tangents = smooth S-curve
  const c = OUTER_R * ARC;
  const pL = cx - plateauHalf; // plateau edges — NOT clamped to the corner, so
  const pR = cx + plateauHalf; // an edge-tab bump keeps its full width.
  const sL = pL - shoulder;
  const sR = pR + shoulder;

  const parts = [`M0 ${h}`, `L0 ${BAR_TOP + OUTER_R}`];

  // LEFT side: rise from the baseline up to the plateau. If the shoulder clears
  // the corner (sL ≥ OUTER_R) draw corner → flat → shoulder; otherwise the bump
  // is near the left edge, so MERGE the corner into the shoulder as one smooth
  // curve (no vertical cusp — the fix for the sharp Rent/Lease corner, #1108).
  if (sL >= OUTER_R) {
    const dL = pL - sL;
    parts.push(
      `C0 ${BAR_TOP + OUTER_R - c} ${OUTER_R - c} ${BAR_TOP} ${OUTER_R} ${BAR_TOP}`,
      `L${sL} ${BAR_TOP}`,
      `C${sL + dL * k} ${BAR_TOP} ${pL - dL * k} ${BUMP_TOP} ${pL} ${BUMP_TOP}`,
    );
  } else {
    // Vertical tangent at the left edge → horizontal tangent at the plateau.
    parts.push(`C0 ${BUMP_TOP} ${pL * (1 - k)} ${BUMP_TOP} ${pL} ${BUMP_TOP}`);
  }

  parts.push(`L${pR} ${BUMP_TOP}`);

  // RIGHT side: mirror.
  if (sR <= w - OUTER_R) {
    const dR = sR - pR;
    parts.push(
      `C${pR + dR * k} ${BUMP_TOP} ${sR - dR * k} ${BAR_TOP} ${sR} ${BAR_TOP}`,
      `L${w - OUTER_R} ${BAR_TOP}`,
      `C${w - OUTER_R + c} ${BAR_TOP} ${w} ${BAR_TOP + OUTER_R - c} ${w} ${BAR_TOP + OUTER_R}`,
    );
  } else {
    parts.push(
      `C${pR + (w - pR) * k} ${BUMP_TOP} ${w} ${BUMP_TOP} ${w} ${BAR_TOP + OUTER_R}`,
    );
  }

  parts.push(`L${w} ${h}`, 'Z');
  return parts.join(' ');
};

export const CurvedTabs = ({ tabs, value, onChange, ariaLabel, className = '' }) => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  const [h, setH] = useState(BASE_H);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const rect = el.getBoundingClientRect();
    setW(rect.width);
    if (rect.height) setH(rect.height);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const box = entries[0].contentRect;
      setW(box.width);
      if (box.height) setH(box.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const count = tabs.length;
  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.value === value),
  );
  const tabW = w ? w / count : 0;
  const cx = tabW * (activeIndex + 0.5);
  // Bigger accent pill, still a touch narrower than the plateau (0.35) so a
  // white rim cradles it.
  const pillHalf = tabW * 0.34;

  return (
    <nav ref={ref} className={`curved-tabs ${className}`.trim()} role="tablist" aria-label={ariaLabel}>
      {/* viewBox width tracks the measured px width (w), so 1 unit = 1px and the
          corner radii render as true circles. No preserveAspectRatio="none": if
          w ever lags the real width (resize), uniform scaling keeps the corners
          ROUND instead of stretching them flat to a sharp angle (#1108). */}
      <svg
        className="curved-tabs__bar"
        width="100%"
        height={h}
        viewBox={`0 0 ${w || 1} ${h}`}
        aria-hidden="true"
      >
        <path className="curved-tabs__fill" d={buildBarPath(w, h, activeIndex, count)} />
        {w > 0 && (
          <rect
            className="curved-tabs__pill"
            x={cx - pillHalf}
            y={h * PILL_Y_R}
            width={pillHalf * 2}
            height={h * PILL_H_R}
            rx={(h * PILL_H_R) / 2}
          />
        )}
      </svg>
      {tabs.map((t) => {
        const isActive = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`curved-tabs__tab${isActive ? ' curved-tabs__tab--active' : ''}`}
            onClick={() => onChange(t.value)}
          >
            {t.label}
          </button>
        );
      })}
    </nav>
  );
};

export default CurvedTabs;
